import apiClient from "@/api/client";
import type {
  AccessMeta,
  GroupFormData,
  Instruction,
  InstructionFormData,
  InstructionGroup,
  InstructionVersion,
} from "../types";

const wrap = <T>(promise: Promise<{ data: { data: T } }>) =>
  promise.then((r) => r.data.data);

export const instructionsApi = {
  // Groups
  listGroups: () =>
    wrap<InstructionGroup[]>(apiClient.get("/instructions/groups/")),

  createGroup: (data: GroupFormData) =>
    wrap<InstructionGroup>(apiClient.post("/instructions/groups/", data)),

  updateGroup: (id: string, data: Partial<GroupFormData>) =>
    wrap<InstructionGroup>(apiClient.patch(`/instructions/groups/${id}/`, data)),

  deleteGroup: (id: string) =>
    apiClient.delete(`/instructions/groups/${id}/`),

  // Instructions
  list: (groupId?: string) =>
    wrap<Instruction[]>(
      apiClient.get("/instructions/", { params: groupId ? { group: groupId } : {} })
    ),

  retrieve: (id: string) =>
    wrap<Instruction>(apiClient.get(`/instructions/${id}/`)),

  create: (data: InstructionFormData) =>
    wrap<Instruction>(apiClient.post("/instructions/", data)),

  update: (id: string, data: Partial<InstructionFormData>) =>
    wrap<Instruction>(apiClient.patch(`/instructions/${id}/`, data)),

  delete: (id: string) =>
    apiClient.delete(`/instructions/${id}/`),

  // Versions
  uploadVersion: (id: string, file: File, changeComment: string) => {
    const form = new FormData();
    form.append("file", file);
    form.append("change_comment", changeComment);
    return wrap<InstructionVersion>(
      apiClient.post(`/instructions/${id}/upload_version/`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      })
    );
  },

  listVersions: (id: string) =>
    wrap<InstructionVersion[]>(apiClient.get(`/instructions/${id}/versions/`)),

  // Bulk ZIP import — returns task_id for polling via bg-tasks API
  importZip: (file: File, onUploadProgress?: (pct: number) => void) => {
    const form = new FormData();
    form.append("file", file);
    return apiClient
      .post<{ data: { task_id: string } }>("/instructions/import-zip/", form, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: onUploadProgress
          ? (e) => onUploadProgress(Math.round((e.loaded * 100) / (e.total ?? 1)))
          : undefined,
      })
      .then((r) => r.data.data);
  },

  // Meta
  getMeta: () => wrap<AccessMeta>(apiClient.get("/instructions/meta/")),
};
